import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  CircularProgress,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import 'react-phone-number-input/style.css';
import { signInWithGoogle } from '../sign-in/SignInGoogle';
import { verifyEmail, verifyOtp } from '../sign-in/SignInWithEmail';
import RegisterModal from './RegisterModal';
import { useAuth } from '../../context/AuthContext';
import '../../styles/styles.css'; 
import Swal from 'sweetalert2';
import OtpModal from './otpModal';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  sx?: object;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [googleUserData, setGoogleUserData] = useState<{
    firstName?: string;
    lastName?: string;
    email: string;
    phoneNumber?: string;
  } | null>(null);
  const { login, googleLogin } = useAuth();

  const handleContinue = async () => {
    setIsLoading(true);
    setError(null);

    try {
        if (email && /\S+@\S+\.\S+/.test(email)) {
          const { status, message }  =  await verifyEmail(email);

        if (status === 404) {
          await Swal.fire({
            title: 'Correo no registrado',
            text: message,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Registrarse',
            cancelButtonText: 'Cancelar',
            customClass: {
              container: 'custom-popup', 
            },
          }).then((result) => {
            if (result.isConfirmed) {
              setGoogleUserData({
                email: email,
              });
              setShowRegisterModal(true);
            }
          });          
        }
        else {
          setShowOtpModal(true);
        }
        } else {
          setError('Por favor, ingresa un correo electrónico válido.');
        } 
    } catch (error) {
      console.error('Error al continuar:', error);
      setError('Ocurrió un error. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);

    try {
      const result = await signInWithGoogle(true);

      if (result) {
        const { response, idToken, userData, /*accessToken*/ } = result;

        googleLogin(idToken, /*accessToken */);

        if (userData) {
          setGoogleUserData({
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email!,
            phoneNumber: userData.phoneNumber!,
          });
        }

        if (response.status === 200) {
          if (response.token) {
            login(response.token);
            onClose();
          }
        } else if (response.status === 201) {
          setShowRegisterModal(true);
        }
      } else {
        console.error('No se recibió una respuesta válida del servidor.');
      }
    } catch (error) {
      console.error('Error de autenticación con Google:', error);
      setError('Ocurrió un error al intentar iniciar sesión con Google.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    setIsLoading(true); 
  
    try {
      const result = await verifyOtp(email, otp);
  
      if (result) {
        const { response, userData, message } = result; 
  
        if (userData) {
          Swal.fire({
            title: 'Éxito',
            text: 'OTP verificado con éxito. Inicio de sesión exitoso.',
            icon: 'success',
            confirmButtonText: 'Aceptar',
            customClass: {
              container: 'custom-popup', 
            },
          }).then(() => {
            setShowOtpModal(false);  
            setEmail('');
            login(response.token);
            onClose();
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: message || 'Hubo un problema al verificar el OTP.',
            icon: 'error',
            confirmButtonText: 'Reintentar',
            customClass: {
              container: 'custom-popup', 
            },
          });
        }
      } else {
        Swal.fire({
          title: 'Error',
          text: 'El código ingresado no es válido.',
          icon: 'error',
          confirmButtonText: 'Reintentar',
          customClass: {
            container: 'custom-popup', 
          },
        });
      }
    } catch (error) {
      console.error('Error al verificar OTP:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al verificar el OTP. Intenta nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: {
          container: 'custom-popup', 
        },
      });
    } finally {
      setIsLoading(false); 
    }
  };
  


  const handleInputChange = () => {
    setError(null);
  };


  return (
    <>
      {/* Modal Principal */}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '80%', sm: '70%', md: '50%', lg: '35%' },
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '90vh',
            overflow: 'auto',
          }}      
        >
            <IconButton
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                color: 'text.primary',
                zIndex: 1,
              }}
              onClick={onClose}
            >
              <Close />
            </IconButton>
          <Box sx={{ position: 'relative' }}>
            <Typography id="auth-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center', fontSize: '22px' }}>
              Iniciar sesión o registrarse
            </Typography>
            <Divider />
          </Box>

          <Box>
            <Typography variant="h5" pt={1} pb={2}>
              Te damos la bienvenida a Alien Realty
            </Typography>

            {!showRegisterModal && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ color: 'green', fontSize: '14px' }}>
                    Correo Electrónico
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      overflow: 'hidden',
                      mb: 4,
                      '& input': {
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        height: '30px',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <TextField
                      type="email"
                      placeholder="Ingresa tu correo electrónico"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        handleInputChange();
                      }}
                      fullWidth
                      error={!!error}
                      helperText={error ? <FormHelperText error>{error}</FormHelperText> : ''}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: 'gray',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'gray' },
                          '&:hover fieldset': { borderColor: 'black' },
                          '&.Mui-focused fieldset': { borderColor: 'green' },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'green', 
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: 'green',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'darkgreen',
                    },
                    position: 'relative',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  fullWidth
                  onClick={handleContinue}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                    />
                  ) : (
                    'Continuar'
                  )}
                </Button>

              </>
            )}

            <Divider sx={{ my: 2 }}>o</Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  backgroundColor: 'white',
                  textAlign: 'center',
                  minHeight: '42px',
                  '& .MuiButton-startIcon': {
                    position: 'absolute',
                    left: '20px',
                  },
                  '&:hover': {
                    backgroundColor: '#e6f4ea',
                    borderColor: 'black',
                    color: 'black',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                  },
                }}
                fullWidth
                startIcon={<img src='/assets/images/google.png' alt="Google Icon" style={{ width: '20px' }} />}
                onClick={handleGoogleSignIn}
              >
                Continúa con Google
              </Button>

            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Registro */}
      {googleUserData && (
        <RegisterModal
          open={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onBack={() => setShowRegisterModal(false)}
          initialData={googleUserData}
        />
      )}

      <OtpModal
        open={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onSubmit={handleVerifyOtp} 
        isLoading={isLoading}      />

    </>
  );
};

export default AuthModal;
